import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Mail,
  Archive,
  ClipboardList,
  Sun,
  Moon,
  User,
  Languages,
  Database,
  Briefcase,
  BarChart3,
  Activity,
  TrendingUp,
  Bot,
  BookOpen,
} from "lucide-react"
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs"
import { useTheme } from "./ThemeProvider"
import { useCounts } from "./CountProvider"
import { useLocale, type Locale } from "./LocaleProvider"
import { useAuth } from "./AuthProvider"

const LOCALES: Locale[] = ["en", "ru", "pl"]

const tabConfig = [
  {
    value: "/dashboard",
    key: "dashboard" as const,
    icon: LayoutDashboard,
    tKey: "nav_dashboard" as const,
  },
  {
    value: "/statistic",
    key: "statistic" as const,
    icon: TrendingUp,
    tKey: "nav_statistic" as const,
  },
  {
    value: "/bot",
    key: "bot" as const,
    icon: Bot,
    tKey: "nav_bot" as const,
    adminOnly: true,
  },
  { value: "/clients", key: "clients" as const, icon: Users, tKey: "nav_clients" as const },
  { value: "/accounts", key: "accounts" as const, icon: CreditCard, tKey: "nav_accounts" as const },
  {
    value: "/matching",
    key: "matching" as const,
    icon: Briefcase,
    tKey: "nav_matching" as const,
  },
  { value: "/mails", key: "mails" as const, icon: Mail, tKey: "nav_mails" as const },
  // {
  //   value: "/gmail",
  //   key: "gmail" as const,
  //   icon: Mail,
  //   tKey: "nav_gmail" as const,
  // },
  {
    value: "/users",
    key: "users" as const,
    icon: Users,
    tKey: "nav_users" as const,
    adminOnly: true,
  },
  {
    value: "/dumps",
    key: "dumps" as const,
    icon: Database,
    tKey: "nav_dumps" as const,
    adminOnly: true,
  },
  {
    value: "/site-visitors",
    key: "visits" as const,
    icon: BarChart3,
    tKey: "nav_site_visitors" as const,
    adminOnly: true,
  },
  {
    value: "/activity",
    key: "activity" as const,
    icon: Activity,
    tKey: "nav_activity" as const,
    adminOnly: true,
  },
  // { value: "/operators", key: "operators" as const, icon: UserCheck, tKey: "nav_operators" as const },
  { value: "/archive", key: "archive" as const, icon: Archive, tKey: "nav_archive" as const },
  { value: "/logs", key: "logs" as const, icon: ClipboardList, tKey: "nav_logs" as const },
  // {
  //   value: "/documentation",
  //   key: "documentation" as const,
  //   icon: BookOpen,
  //   tKey: "nav_documentation" as const,
  // },
]

function Header() {
  const location = useLocation()
  const navigate = useNavigate()
  const { theme, toggleTheme } = useTheme()
  const { counts } = useCounts()
  const { t, locale, setLocale } = useLocale()
  const { user, logout } = useAuth()
  const [tabValue, setTabValue] = useState(location.pathname)

  useEffect(() => {
    setTabValue(location.pathname)
  }, [location.pathname])

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center px-6">
        <Tabs
          value={tabValue}
          onValueChange={(value) => {
            setTabValue(value)
            setTimeout(() => navigate(value), 0)
          }}
          className="flex-1"
        >
          <TabsList className="h-9">
            {tabConfig
              .filter((tab) => !tab.adminOnly || user?.role === "ADMIN")
              .map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="gap-2">
                  <tab.icon className="h-4 w-4" />
                  {t(tab.tKey)}
                  {counts[tab.key] !== undefined && (
                    <span className="ml-1 text-xs text-muted-foreground">({counts[tab.key]})</span>
                  )}
                </TabsTrigger>
              ))}
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            <span className="font-medium text-foreground">{user?.fullName || "—"}</span>
          </div>

          <button
            onClick={() => setLocale(LOCALES[(LOCALES.indexOf(locale) + 1) % LOCALES.length])}
            className="h-8 rounded-md border border-input bg-background px-2.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            {locale.toUpperCase()}
          </button>

          <button
            onClick={toggleTheme}
            className="h-9 w-9 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </button>

          <button
            onClick={logout}
            className="h-8 rounded-md border border-input bg-background px-2.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            {t("btn_logout")}
          </button>
        </div>
      </div>
    </header>
  )
}

export default Header
